import { useEffect } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { captureMessage } from '@leagueplatform/observability';
import { useGetUserProfile } from './use-get-user-profile.hook';

// The SE's stored demo selection. `persona_info` is written server-side by the
// setup screen (MSP3-301) and is not in `@leagueplatform/user-profile-api`'s
// published types yet, so it is narrowed locally — the same approach
// `use-is-caregiver.hook` takes for the custom ID-token claim.
type PersonaInfoView = {
  persona_info?: {
    persona_id?: string;
    market?: string;
  };
};

// Market slug -> locale key. Hardcoded per the bet's Aug 21 decision that the
// market list may live in the EOS app for this bet. The server's Contentful
// map is what gives a market meaning; this only decides how to name it.
const MARKET_LABEL_IDS: Partial<Record<string, `STR_${string}`>> = {
  us_payer: 'STR_MARKET_US_PAYER',
  us_provider: 'STR_MARKET_US_PROVIDER',
};

type SelectedMarket = {
  /** Display label, or `undefined` when there is nothing safe to show. */
  label?: string;
  /** Whether a known market resolved. Logged as a bool, never as the slug. */
  resolved: boolean;
  /**
   * Whether the profile read has finished, either way. `resolved` is `false`
   * while still loading, which is indistinguishable from a genuine failure —
   * callers that log an outcome should wait for this before reporting one.
   */
  settled: boolean;
};

/**
 * The display label for the SE's currently selected demo market.
 *
 * Fails open in every failure mode — no selection, a failed profile read, or a
 * market this build does not recognise all yield no label, so callers render
 * their row bare rather than blocking or erroring.
 *
 * Deliberately reports `resolved` as a boolean rather than returning the raw
 * slug for logging. The server treats `Market` as caller-controlled and keeps
 * it out of plaintext logs (see `up.Market` in services); mirroring that here
 * keeps the client from undoing it.
 */
export const useSelectedMarket = (): SelectedMarket => {
  const { formatMessage } = useIntl();
  const { data, error } = useGetUserProfile();

  const market = (data as (typeof data & PersonaInfoView) | undefined)
    ?.persona_info?.market;
  const labelId = market ? MARKET_LABEL_IDS[market] : undefined;

  // A stored market this build cannot name means the app is older than the
  // catalog, not that the selection is broken.
  const isUnrecognised = Boolean(market) && !labelId;

  useEffect(() => {
    if (error) {
      captureMessage('Failed to read profile for market badge', {
        severityLevel: 'warning',
        context: { personaSelector: { error: String(error) } },
      });
    }
  }, [error]);

  useEffect(() => {
    if (isUnrecognised) {
      captureMessage('Unrecognised market stored on profile', {
        severityLevel: 'warning',
        context: { personaSelector: { marketResolved: false } },
      });
    }
  }, [isUnrecognised]);

  // Either branch of the profile read having landed is enough; which one it was
  // is already reflected in `resolved`.
  const settled = data !== undefined || error !== undefined;

  if (!labelId) return { resolved: false, settled };

  return { label: formatMessage({ id: labelId }), resolved: true, settled };
};
